import { useEffect, useMemo } from 'react';
import { useAsync } from 'react-use';

import { Entity, stringifyEntityRef } from '@backstage/catalog-model';
import { errorApiRef, useApi } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';

import { DaaSType } from '../types';
import { listEntitiesOwnedByUserGroups } from './ownedEntities';
import { useDaaSEditableEntities } from './useDaaSEditableEntities';
import { useGetUserGroups } from './useGetUserGroups';

const FIELDS = [
  'metadata.title',
  'metadata.name',
  'metadata.namespace',
  'metadata.description',
  'metadata.annotations',
  'kind',
  'spec',
  'relations',
];

/**
 * Lists the DaaS catalog Components of one `spec.type`.
 *
 * @param entityType - the DaaS `spec.type` to list (`daas-project`,
 *   `daas-application` or `daas-application-set`)
 * @param userOwnedEntities - when `true`, only entities owned by one of the
 *   caller's own supervisory-org groups are listed
 * @param userInSecurityGroup - when `true`, the list is narrowed to the
 *   entities `daas-backend` reports the caller may edit
 */
export const useGetDaasEntities = (
  entityType: DaaSType,
  userOwnedEntities: boolean = false,
  userInSecurityGroup: boolean = false,
) => {
  const errorApi = useApi(errorApiRef);
  const catalogApi = useApi(catalogApiRef);

  const {
    value: userGroups,
    loading: userGroupsLoading,
    error: userGroupsError,
  } = useGetUserGroups();

  const {
    editableRefs,
    loading: editableLoading,
    error: editableError,
  } = useDaaSEditableEntities(entityType, { enabled: userInSecurityGroup });

  const {
    value: entities,
    loading,
    error,
  } = useAsync(async () => {
    if (userOwnedEntities) {
      return listEntitiesOwnedByUserGroups({
        catalogApi,
        errorApi,
        userGroups,
        userGroupsLoading,
        userGroupsError,
        filter: { kind: 'Component', 'spec.type': entityType },
        fields: FIELDS,
      });
    }

    const { items } = await catalogApi.getEntities({
      filter: {
        kind: 'Component',
        'spec.type': entityType,
      },
      fields: FIELDS,
    });

    return items;
  }, [
    entityType,
    userOwnedEntities,
    userGroupsLoading,
    userGroups,
    userGroupsError,
  ]);

  useEffect(() => {
    if (userInSecurityGroup && editableError) {
      errorApi.post(editableError);
    }
  }, [userInSecurityGroup, editableError, errorApi]);

  // When userInSecurityGroup is enabled, keep only the entities the daas-backend
  // reports the caller may edit. Fails closed: empty while still resolving or on
  // error.
  const filteredEntities = useMemo(() => {
    if (!userInSecurityGroup || !entities) return entities;
    if (editableLoading) return undefined;
    if (editableError) return [];
    return entities.filter((entity: Entity) =>
      editableRefs.has(stringifyEntityRef(entity)),
    );
  }, [
    userInSecurityGroup,
    entities,
    editableLoading,
    editableError,
    editableRefs,
  ]);

  return {
    value: userInSecurityGroup ? filteredEntities : entities,
    loading: loading || (userInSecurityGroup && editableLoading),
    error: error || editableError,
  };
};
